/**
 * Hashtag parsing for note text.
 *
 * `extractHashtags` produces the normalized `tags` array; `tokenizeHashtags`
 * produces loss-less segments for rendering inline highlights. Both share one
 * regex so the matching rule never diverges between extraction and rendering.
 *
 * The matching rule MUST stay in sync with `extract_hashtags` in
 * `apps/api/src/app/text/hashtags.py`. The shared test corpus
 * (`hashtags.test.ts` and `tests/test_hashtags.py`) guards against drift.
 *
 * A hashtag is a `#` at the start of the text or preceded by a non-word
 * character (so `word#tag` does not match, but `word #tag` does), followed by
 * 1-50 letters / digits / underscores. Extracted tags are lower-cased,
 * de-duplicated preserving first-appearance order, and capped at `MAX_TAGS`.
 */

// Group 1 = (start-of-string | a single non-word separator), group 2 = tag body.
export const HASHTAG_PATTERN = '(^|[^\\p{L}\\p{N}_])#([\\p{L}\\p{N}_]{1,50})'

export const MAX_TAGS = 30

export interface TextSegment {
  text: string
  isHashtag: boolean
}

/** Normalized, de-duplicated hashtags found in `text` (lower-cased, no `#`). */
export function extractHashtags(text: string): string[] {
  if (!text) return []

  const regex = new RegExp(HASHTAG_PATTERN, 'gu')
  const seen = new Set<string>()
  const tags: string[] = []
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    const tag = match[2]!.toLowerCase()
    if (!seen.has(tag)) {
      seen.add(tag)
      tags.push(tag)
      if (tags.length >= MAX_TAGS) break
    }
  }
  return tags
}

/**
 * Split `text` into ordered segments for rendering. Loss-less:
 * `tokenizeHashtags(text).map((s) => s.text).join('') === text`.
 */
export function tokenizeHashtags(text: string): TextSegment[] {
  if (!text) return []

  const regex = new RegExp(HASHTAG_PATTERN, 'gu')
  const segments: TextSegment[] = []
  let cursor = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    const separator = match[1] ?? ''
    const hashStart = match.index + separator.length
    // Everything up to and including the separator is plain text.
    if (hashStart > cursor) {
      segments.push({ text: text.slice(cursor, hashStart), isHashtag: false })
    }
    const end = match.index + match[0].length
    segments.push({ text: text.slice(hashStart, end), isHashtag: true })
    cursor = end
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), isHashtag: false })
  }
  return segments
}
